import { useState } from "react";
import { getCanonicalLocation, regionFromCountry } from "@/lib/canonicalLocation";
import { detectRegion, type Region } from "@/lib/regionDetection";

const VALID_REGIONS: Region[] = ["US", "SPAIN", "LATAM", "OTHER"];

/**
 * Región de negocio (LATAM/US/SPAIN/OTHER) — derivada de la VERDAD ÚNICA de
 * location (país del Worker vía canonicalLocation). El fallback por browser
 * (detectRegion) corre SOLO cuando el Worker no resolvió país (dev local sin
 * Worker); antes era la fuente primaria y encima persistía en localStorage,
 * dejando pegada para siempre una detección errada de la primera visita.
 *
 * `setRegion` es un override MANUAL (switcher de UI) y es lo único que se
 * persiste (key user_region_manual). La key legacy user_region ya no se
 * escribe automáticamente.
 */
export const useRegion = () => {
  const [region, setRegionState] = useState<Region>(() => {
    if (typeof window === "undefined") return "LATAM";

    const manual = localStorage.getItem("user_region_manual") as Region | null;
    if (manual && VALID_REGIONS.includes(manual)) return manual;

    const canonical = regionFromCountry(getCanonicalLocation().country);
    if (canonical) return canonical;

    // Sin Worker (dev local): heurística de browser como último recurso.
    return detectRegion();
  });

  const setRegion = (next: Region) => {
    if (typeof window !== "undefined" && VALID_REGIONS.includes(next)) {
      localStorage.setItem("user_region_manual", next);
    }
    setRegionState(next);
  };

  return { region, setRegion };
};
